import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Search, Globe, Loader2 } from "lucide-react";
import { format } from "date-fns";

interface NewAnalysisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PreviousAnalysis {
  id: string;
  url: string;
  results: unknown;
  created_at: string;
}

export function NewAnalysisDialog({ open, onOpenChange }: NewAnalysisDialogProps) {
  const [url, setUrl] = useState("");
  const [previousAnalyses, setPreviousAnalyses] = useState<PreviousAnalysis[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (open && user) {
      fetchPreviousAnalyses();
    }
  }, [open, user]);

  const fetchPreviousAnalyses = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("analyses")
      .select("id, url, results, created_at")
      .order("created_at", { ascending: false })
      .limit(10);
    setPreviousAnalyses((data as PreviousAnalysis[]) ?? []);
    setLoading(false);
  };

  const handleAnalyze = () => {
    if (!url.trim()) return;
    const email = user?.email ?? "";
    navigate(`/analyze?url=${encodeURIComponent(url.trim())}&email=${encodeURIComponent(email)}`);
    onOpenChange(false);
    setUrl("");
  };

  const handleSelectPrevious = (analysis: PreviousAnalysis) => {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(analysis.results))));
    const email = user?.email ?? "";
    navigate(`/report?data=${encodeURIComponent(encoded)}&email=${encodeURIComponent(email)}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Analysis</DialogTitle>
          <DialogDescription>
            Enter a URL to analyze or select a previous analysis.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex gap-2">
            <Input
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
            />
            <Button onClick={handleAnalyze} disabled={!url.trim()}>
              Analyze
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              or select a previous analysis
            </span>
            <Separator className="flex-1" />
          </div>

          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : previousAnalyses.length > 0 ? (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {previousAnalyses.map((analysis) => (
                <button
                  key={analysis.id}
                  onClick={() => handleSelectPrevious(analysis)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-muted/50 transition-colors"
                >
                  <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{analysis.url}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(analysis.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-muted/30 p-6 text-center">
              <Search className="mx-auto h-5 w-5 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No previous analyses yet.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
